/**
 * Fuente única de los destinos del cliente por rol y grupo (interaction.md §1 y §7).
 * El panel observa; la operativa vive en el chat MCP. La UI decide «Sin permiso» por el rol de
 * `/auth/me`, no por el código HTTP (contracts/panel-api.md §1.4).
 */
import type { MemberRole } from "@/api/schemas";

export type Rol = MemberRole;

export type DestinoId =
  | "inicio" | "leads" | "citas" | "whatsapp" | "experiencias"
  | "propuestas" | "campanas" | "resultados" | "fisicas" | "landings"
  | "conexiones" | "equipo" | "ajustes";

type GrupoId = "dia" | "publicidad" | "cliente";

export interface Destino {
  id: DestinoId;
  /** Sufijo bajo `/c/:cliente/`. */
  ruta: string;
  label: string;
  /** Rótulo corto para la barra inferior y los espacios estrechos. */
  shortLabel?: string;
  grupo: GrupoId | null;
  roles: readonly Rol[];
}

const AGENCIA: readonly Rol[] = ["agency"];
const TODOS: readonly Rol[] = ["agency", "store"];

export const DESTINOS: readonly Destino[] = [
  { id: "inicio", ruta: "inicio", label: "Inicio", grupo: null, roles: TODOS },
  { id: "leads", ruta: "leads", label: "Leads", grupo: "dia", roles: TODOS },
  { id: "citas", ruta: "citas", label: "Citas", grupo: "dia", roles: TODOS },
  { id: "whatsapp", ruta: "whatsapp", label: "WhatsApp", grupo: "dia", roles: TODOS },
  { id: "propuestas", ruta: "propuestas", label: "Propuestas", grupo: "publicidad", roles: AGENCIA },
  { id: "campanas", ruta: "campanas", label: "Campañas", grupo: "publicidad", roles: AGENCIA },
  { id: "resultados", ruta: "resultados", label: "Resultados", grupo: "publicidad", roles: TODOS },
  // Va tras Resultados para que la tienda conserve sus atajos 1–5; la agencia lo ve en «Día a día» tras WhatsApp.
  { id: "experiencias", ruta: "experiencias", label: "Experiencias", grupo: "dia", roles: TODOS },
  { id: "fisicas", ruta: "campanas/fisicas", label: "Campañas físicas", shortLabel: "Físicas", grupo: "publicidad", roles: AGENCIA },
  { id: "landings", ruta: "landings", label: "Landings", grupo: "publicidad", roles: AGENCIA },
  { id: "conexiones", ruta: "conexiones", label: "Conexiones", grupo: "cliente", roles: AGENCIA },
  { id: "equipo", ruta: "equipo", label: "Equipo", grupo: "cliente", roles: AGENCIA },
  { id: "ajustes", ruta: "ajustes", label: "Ajustes", grupo: "cliente", roles: AGENCIA },
];

const TITULO_GRUPO: Record<GrupoId, string> = { dia: "Día a día", publicidad: "Publicidad", cliente: "Cliente" };

export function destinoPorId(id: DestinoId): Destino {
  const destino = DESTINOS.find((d) => d.id === id);
  if (!destino) throw new Error(`Destino desconocido: ${id}`);
  return destino;
}

export function destinosDeRol(rol: Rol): Destino[] {
  return DESTINOS.filter((d) => d.roles.includes(rol));
}

export interface GrupoNav {
  titulo: string | null;
  destinos: Destino[];
}

/** Los grupos de la barra lateral, sin Inicio (va suelto arriba). La tienda no lleva títulos de grupo. */
export function gruposDeRol(rol: Rol): GrupoNav[] {
  const destinos = destinosDeRol(rol).filter((d) => d.grupo !== null);
  if (rol === "store") return [{ titulo: null, destinos }];
  return (Object.keys(TITULO_GRUPO) as GrupoId[]).map((grupo) => ({
    titulo: TITULO_GRUPO[grupo],
    destinos: destinos.filter((d) => d.grupo === grupo),
  }));
}

/** Atajos `1`–`9` (interaction.md §1.2), fuera de los campos de texto. */
export const ATAJOS: Record<Rol, readonly DestinoId[]> = {
  agency: ["inicio", "leads", "citas", "whatsapp", "propuestas", "campanas", "resultados", "landings", "conexiones"],
  store: ["inicio", "leads", "citas", "whatsapp", "resultados"],
};

export const BARRA_INFERIOR: Record<Rol, { principales: readonly DestinoId[]; conMas: boolean }> = {
  agency: { principales: ["inicio", "leads", "citas", "whatsapp"], conMas: true },
  store: { principales: ["inicio", "leads", "citas", "whatsapp", "resultados", "experiencias"], conMas: false },
};

interface SeccionDeRuta {
  destino: DestinoId;
  /** Título de la sección en `document.title`: el de la pantalla concreta, no solo el del destino. */
  titulo: string;
  rolesPermitidos: readonly Rol[];
}

// De más específica a más general: la primera que sea prefijo (por segmentos) de la ruta gana.
const SECCIONES: ReadonlyArray<readonly [prefijo: string, SeccionDeRuta]> = [
  ["experiencias/ajustes", { destino: "experiencias", titulo: "Ajustes de Experiencias", rolesPermitidos: AGENCIA }],
  ["experiencias/muestras", { destino: "experiencias", titulo: "Muestras de calidad", rolesPermitidos: TODOS }],
  ["experiencias/solicitudes", { destino: "experiencias", titulo: "Solicitud", rolesPermitidos: TODOS }],
  ["propuestas/historial", { destino: "propuestas", titulo: "Historial", rolesPermitidos: AGENCIA }],
  ["propuestas/embudos", { destino: "propuestas", titulo: "Embudos", rolesPermitidos: AGENCIA }],
  ["campanas/fisicas", { destino: "fisicas", titulo: "Campañas físicas", rolesPermitidos: AGENCIA }],
  ["ajustes/negocio", { destino: "ajustes", titulo: "Tu negocio", rolesPermitidos: AGENCIA }],
  ["ajustes/avanzado", { destino: "ajustes", titulo: "Avanzado", rolesPermitidos: AGENCIA }],
  ...DESTINOS.filter((d) => !d.ruta.includes("/")).map(
    (d): readonly [string, SeccionDeRuta] => [d.ruta, { destino: d.id, titulo: d.label, rolesPermitidos: d.roles }],
  ),
];

function empiezaPorSegmentos(ruta: string, prefijo: string): boolean {
  return ruta === prefijo || ruta.startsWith(`${prefijo}/`);
}

/** Sección a la que pertenece un sufijo de `/c/:cliente/…` (`null` si no existe). */
export function seccionDeRuta(ruta: string): SeccionDeRuta | null {
  const limpia = ruta.replace(/^\/+|\/+$/g, "");
  return SECCIONES.find(([prefijo]) => empiezaPorSegmentos(limpia, prefijo))?.[1] ?? null;
}

export function puedeVerRuta(rol: Rol, ruta: string): boolean {
  return seccionDeRuta(ruta)?.rolesPermitidos.includes(rol) ?? false;
}

export function rutaDeCliente(slug: string, ruta: string): string {
  return `/c/${encodeURIComponent(slug)}/${ruta}`;
}

/** Secciones canónicas (sin parámetros de ruta) a las que se puede saltar al cambiar de cliente. */
export const RUTAS_ESTATICAS: readonly string[] = [
  ...DESTINOS.map((d) => d.ruta),
  "propuestas/historial", "ajustes/negocio", "ajustes/avanzado", "experiencias/muestras", "experiencias/ajustes",
];
